import { CalendarItemType } from '@akin-home/amplify';
import {
  CalendarItemReminderType,
  getDefaultReminders,
} from '@akin-home/calendar-reminders';
import { startDatePast } from '../../calendar/common/actions';
import { createCalendarChatItem } from '../../chat/ChatItem/functions';
import { CalendarItem, Meal } from '../../models';
import { AppStateSlice } from '../../stores/app_state';
import { NotificationItem, ReminderStore } from './types';

export const createReminderStore: AppStateSlice<ReminderStore> = (
  set,
  get
) => ({
  active_notifications: [],
  active_alarms: [],
  snoozed_alarms: [],

  upcoming_calendar_items: {
    alarms: [],
    alerts: [],
    events: [],
    meals: [],
  },

  setUpcomingCalendarItems: (type, items) => {
    switch (type) {
      case CalendarItemType.Alarm:
        set((state) => ({
          upcoming_calendar_items: {
            ...state.upcoming_calendar_items,
            alarms: items,
          },
        }));
        break;
      case CalendarItemType.Alert:
        set((state) => ({
          upcoming_calendar_items: {
            ...state.upcoming_calendar_items,
            alerts: items,
          },
        }));
        break;
      case CalendarItemType.Event:
        set((state) => ({
          upcoming_calendar_items: {
            ...state.upcoming_calendar_items,
            events: items,
          },
        }));
        break;
      case CalendarItemType.Meal:
        set((state) => ({
          upcoming_calendar_items: {
            ...state.upcoming_calendar_items,
            meals: items,
          },
        }));
        break;

      default:
        break;
    }
  },

  dismissNotification: (item: NotificationItem) => {
    set((state) => {
      state.active_notifications = state.active_notifications?.filter(
        (notification) => notification.id !== item.id
      );
    });
  },
  dismissAlarm: (item: NotificationItem) => {
    set((state) => {
      state.active_alarms = state.active_alarms?.filter(
        (alarm) => alarm.id !== item.id
      );
    });
  },
  snoozeAlarm: (item: NotificationItem) => {
    set((state) => {
      state.active_alarms = state.active_alarms?.filter(
        (alarm) => alarm.id !== item.id
      );

      const snooze_alarm_timer_id = setTimeout(() => {
        set((state) => {
          state.active_alarms = [item, ...state.active_alarms];
          state.snoozed_alarms.filter(({ alarm }) => alarm.id === item.id);
        });
      }, 1000 * 60 * 5);

      // Add alarm to snooze list
      state.snoozed_alarms.push({
        alarm: item,
        timer_id: snooze_alarm_timer_id,
      });
    });
  },

  startReminderListener: () => {
    const reminder_listener_interval_id = setInterval(() => {
      set((state) => {
        // I have all the items from upcoming_calendar_items
        // What I want to do is seperate it up into, active_notifications, active_alerts and upcoming_calendar_items

        const new_notifications: NotificationItem[] = [];
        const new_alarms: NotificationItem[] = [];
        const new_meals: Meal[] = [];

        // TODO: This does not currently take into account the secondsOffset field of the reminder
        // TODO: This currently only processes the first item in the reminders list.
        const extractActiveItems = (items?: CalendarItem[]) => {
          if (!items) {
            return [];
          }

          return items.filter((item) => {
            // If item is in the future, ignore it and keep it in array
            if (!startDatePast(item)) {
              return true;
            }

            /** Items from here on in are in the past and need to be processed + removed from array */

            // Meals are treated seperate to notifications and alarms
            if (item.type === CalendarItemType.Meal) {
              new_meals.push(item);
              return false;
            }

            const reminders = item?.reminders
              ? item?.reminders
              : getDefaultReminders(item.type);
            const reminder = reminders?.[0];

            if (reminder?.type === CalendarItemReminderType.Notification) {
              new_notifications.push(item);
            } else if (reminder?.type === CalendarItemReminderType.Alarm) {
              new_alarms.push(item);
            }
            return false;
          });
        };

        state.upcoming_calendar_items = {
          alarms: extractActiveItems(state.upcoming_calendar_items.alarms),
          alerts: extractActiveItems(state.upcoming_calendar_items.alerts),
          events: extractActiveItems(state.upcoming_calendar_items.events),
          meals: extractActiveItems(state.upcoming_calendar_items.meals),
        };

        if (new_meals?.length) {
          const latest_meal = new_meals[new_meals.length - 1];

          /** Add the meal to the chat, it will be displayed as a notification item */
          get().addChatItem(
            createCalendarChatItem({
              items: [{ type: latest_meal.type, item_id: latest_meal.id }],
            })
          );
        }

        // Add new items and sort by date in descending order
        state.active_notifications = new_notifications
          .concat(state.active_notifications)
          .sort(
            (elem1, elem2) => elem2.start.getTime() - elem1.start.getTime()
          );
        state.active_alarms = new_alarms
          .concat(state.active_alarms)
          .sort(
            (elem1, elem2) => elem2.start.getTime() - elem1.start.getTime()
          );
      });
    }, 5000);

    set({ reminder_listener_interval_id });
  },
  stopReminderListener: () => {
    const interval_id = get().reminder_listener_interval_id;
    if (interval_id) {
      clearInterval(interval_id);
    }
    set({ reminder_listener_interval_id: undefined });
  },
});
